import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import teacherService from '../services/teacherService';
import './FindTeachers.css';

const FindTeachers = () => {
  const { t } = useTranslation();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    subjects: [],
    languages: [],
    minPrice: '',
    maxPrice: '',
    sortBy: 'rating'
  });

  useEffect(() => {
    loadTeachers();
  }, [filters]);

  const loadTeachers = async () => {
    try {
      setLoading(true);
      const result = await teacherService.getAllTeachers(filters);
      setTeachers(result);
    } catch (error) {
      console.error('Load teachers error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      try {
        setLoading(true);
        const result = await teacherService.searchTeachers(searchTerm);
        setTeachers(result);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    } else {
      loadTeachers();
    }
  };

  const filteredTeachers = teachers.filter(teacher => {
    if (searchTerm) {
      const fullName = `${teacher.firstName} ${teacher.lastName}`.toLowerCase();
      const bio = teacher.bio?.toLowerCase() || '';
      const term = searchTerm.toLowerCase();
      return fullName.includes(term) || bio.includes(term);
    }
    return true;
  });

  return (
    <div className="find-teachers-page">
      <div className="container">
        <div className="page-header">
          <h1>{t('teachers.findTeacher')}</h1>
          <p>Connect with expert tutors for personalized online lessons</p>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="search-section">
          <div className="search-bar-large">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, subject, or skill..."
              className="search-input-large"
            />
            <button type="submit" className="btn btn-primary btn-lg">
              🔍 Search
            </button>
          </div>
        </form>

        {/* Content */}
        {loading ? (
          <div className="loading-section">
            <div className="spinner"></div>
            <p>Loading teachers...</p>
          </div>
        ) : filteredTeachers.length === 0 ? (
          <div className="no-results">
            <h2>No teachers found</h2>
            <p>Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="teachers-grid">
            {filteredTeachers.map((teacher) => (
              <Link
                key={teacher.id}
                to={`/teacher/${teacher.id}`}
                className="teacher-card"
              >
                <div className="teacher-card-header">
                  <div className="teacher-avatar">
                    {teacher.photoURL ? (
                      <img src={teacher.photoURL} alt={teacher.firstName} />
                    ) : (
                      <div className="avatar-placeholder">
                        {teacher.firstName?.charAt(0)}
                      </div>
                    )}
                  </div>
                  {teacher.videoIntroUrl && (
                    <span className="video-badge">🎥</span>
                  )}
                </div>

                <div className="teacher-card-body">
                  <h3>{teacher.firstName} {teacher.lastName}</h3>
                  
                  {teacher.subjects.length > 0 && (
                    <div className="teacher-subjects">
                      {teacher.subjects.slice(0, 3).map((subject, idx) => (
                        <span key={idx} className="subject-tag">
                          {subject}
                        </span>
                      ))}
                      {teacher.subjects.length > 3 && (
                        <span className="subject-tag more">
                          +{teacher.subjects.length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  {teacher.bio && (
                    <p className="teacher-bio">
                      {teacher.bio.substring(0, 100)}
                      {teacher.bio.length > 100 && '...'}
                    </p>
                  )}

                  <div className="teacher-meta">
                    <div className="rating">
                      ⭐ {teacher.rating > 0 ? teacher.rating.toFixed(1) : 'New'}
                      {teacher.reviewCount > 0 && (
                        <span className="review-count">({teacher.reviewCount})</span>
                      )}
                    </div>
                    <div className="lessons-count">
                      {teacher.lessonsCompleted || 0} lessons
                    </div>
                  </div>

                  <div className="teacher-footer">
                    <div className="teacher-languages">
                      {teacher.languages.slice(0, 3).join(', ')}
                    </div>
                    <div className="teacher-price">
                      £{teacher.hourlyRate}/hr
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {filteredTeachers.length > 0 && (
          <div className="results-info">
            Showing {filteredTeachers.length} teacher{filteredTeachers.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </div>
  );
};

export default FindTeachers;